import { FloatingTooltip } from 'components/floating-tooltip';
import { LaunchLink } from 'components/launch-link';
import { formatNumber } from 'utils/number';
import style from './global-settings.module.scss';
import {
  Button,
  DatePicker,
  DatePickerInput,
  ComboBox,
  Dropdown,
  InlineLoading,
  Layer,
  SkeletonText,
  RadioButtonGroup,
  RadioButton,
  InlineNotification,
  DefinitionTooltip,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@carbon/react';
import { Checkmark, Download, Information, Renew } from '@carbon/react/icons';
import { WorkflowStatusPopover } from 'components/workflow-status-popover/workflow-status-popover';
import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import {
  FreeCashFlowAggregationPeriod,
  MeasurementType,
  SlotVolumeSource,
  SlotAfeSource,
} from 'types/evaluation';
import { capitalize } from 'lodash-es';
import {
  parseMomentUtc,
  SERVER_DATE_FORMAT,
  US_DATE_FORMAT,
} from 'utils/dates';
import moment from 'moment';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import { DateRangeFilter } from './date-range-filter';
import { useAtomValue } from 'jotai';
import {
  workingDateRangeFilterTypeAtom,
  workingAbsoluteMinDateAtom,
  workingAbsoluteMaxDateAtom,
  type DpsScheduleStatus,
  type EvaluationCaseDetail,
  type EvaluationCaseListItem,
  type EvalMode,
} from '../../eval-3-workspace/atoms';
import type { ScenariosResponse } from 'types/api-responses';
import type { TaxScenarioItem } from '../../eval-3-workspace/types';
import { EvaluationCaseDomainChips } from '../../eval-3-workspace/evaluation-case-domain-chips';
import { capitalizeFirstLetter } from 'utils/strings';
import { isCaseRun } from '../../eval-3-workspace/util';

interface Eval2RunItem {
  id: number;
  scenario: string;
  interestType: string;
  user: string;
  runDate: string;
}

type PriceDeckItem = { id: number; name: string };

const SCENARIO_PRICING_ITEM: PriceDeckItem = {
  id: 0,
  name: 'Scenario Pricing',
};

interface DpsItem {
  id: number;
  name: string;
  paceModel: string;
  referenceDate: string;
}

interface DrsItem {
  id: number;
  name: string;
  effectiveDate: string | null;
}

interface DifferentialScenarioItem {
  id: number;
  name: string;
  effectiveDate: string | null;
}

interface ExpenseScenarioItem {
  id: number;
  name: string;
  effectiveDate: string | null;
}

type DcaScenarioDropdownItem = {
  id: number | null;
  name: string;
  href?: string;
};

const LEGACY_DCA_ITEM: DcaScenarioDropdownItem = { id: null, name: 'Legacy' };

interface Props {
  evalId: number;
  packageId: number;
  scenarioName?: string;
  scenarioHref?: string;
  isLoading: boolean;
  effectiveDate: string;
  appliedEffectiveDate: string;
  setEffectiveDate: (date: string) => void;
  aggregation: FreeCashFlowAggregationPeriod;
  setAggregation: (aggregation: FreeCashFlowAggregationPeriod) => void;
  measurementType: MeasurementType;
  setMeasurementType: (measurementType: MeasurementType) => void;
  slotVolumeSource: SlotVolumeSource;
  setSlotVolumeSource: (slotVolumeSource: SlotVolumeSource) => void;
  slotAfeSource: SlotAfeSource;
  setSlotAfeSource: (slotAfeSource: SlotAfeSource) => void;
  eval2Runs?: Eval2RunItem[];
  selectedEval2Id?: number | null;
  onEval2Change?: (id: number) => void;
  // INS-8827: Scenario picker — sourced from the parent which subscribes
  // to the global Scenario index. Selection is propagated up so the
  // calculate / DPS schedule requests can carry the picked scenario_id.
  scenarios?: ScenariosResponse[];
  selectedScenarioId?: number | null;
  onScenarioChange?: (id: number | null) => void;
  dpsList?: DpsItem[];
  selectedDpsId?: number | null;
  onDpsChange?: (id: number) => void;
  dpsSlotCount?: number | null;
  dpsScheduleStatus?: DpsScheduleStatus;
  snapshotStatusItems?: StatusItem[];
  snapshotOverallStatus?: string;
  onDpsShowDetails?: () => void;
  drsList?: DrsItem[];
  selectedDrsId?: number | null;
  onDrsChange?: (id: number | null) => void;
  differentialScenarioList?: DifferentialScenarioItem[];
  selectedDifferentialScenarioId?: number | null;
  onDifferentialScenarioChange?: (id: number | null) => void;
  taxScenarioList?: TaxScenarioItem[];
  selectedTaxScenarioId?: number | null;
  onTaxScenarioChange?: (id: number | null) => void;
  expenseScenarioList?: ExpenseScenarioItem[];
  selectedExpenseScenarioId?: number | null;
  onExpenseScenarioChange?: (id: number | null) => void;
  priceDecks?: PriceDeckItem[];
  selectedPriceDeckId?: number | null;
  onPriceDeckChange?: (id: number | null) => void;
  onShowPriceDeckDetails?: () => void;
  onExportAries?: () => void;
  isExportAriesDisabled?: boolean;
  isExportAriesLoading?: boolean;
  exportAriesTooltip?: string;
  // Aries MDB export (INS-10553). `onExportAriesMdb` opens the config modal
  // (owned by the parent, which POSTs the edited config to the kickoff).
  // Progress is shown only inside the modal — no page-level status icon.
  onExportAriesMdb?: () => void;
  /** Triggers a full export of the Slot Allocations content (all rows/columns). */
  onExportContent?: () => void;
  isExportContentDisabled?: boolean;
  isExportContentLoading?: boolean;
  exportContentTooltip?: string;
  dcaScenarios?: DcaScenarioDropdownItem[];
  selectedDcaScenarioId?: number | null;
  onDcaScenarioChange?: (id: number | null) => void;
  // Evaluation Case mode (INS-9947)
  evalMode?: EvalMode;
  onEvalModeChange?: (mode: EvalMode) => void;
  evaluationCaseList?: EvaluationCaseListItem[];
  selectedEvaluationCaseId?: number | null;
  onEvaluationCaseChange?: (id: number | null) => void;
  evaluationCaseDetail?: EvaluationCaseDetail | null;
}

/**
 * Global settings panel for the Eval 3.0 FCF view. Contains radio buttons
 * for measurement basis and AFE source, plus date picker and time interval
 * dropdown. Optionally renders an Eval 2.0 run dropdown.
 *
 * @param {Props} props - Component props.
 * @return {JSX.Element} The global settings panel.
 */
export function GlobalSettings({
  evalId,
  packageId,
  isLoading,
  effectiveDate,
  appliedEffectiveDate,
  setEffectiveDate,
  aggregation,
  setAggregation,
  measurementType,
  setMeasurementType,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  slotVolumeSource,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSlotVolumeSource,
  slotAfeSource,
  setSlotAfeSource,
  // eval2Runs / selectedEval2Id / onEval2Change kept on the prop contract for
  // parent compatibility — the calculate request still consumes the eval2 id
  // via the atom layer. The UI surfacing of the eval2 picker has been
  // replaced with a scenario picker (UI only; BE wiring TBD).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  eval2Runs,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedEval2Id,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onEval2Change,
  scenarios,
  selectedScenarioId,
  onScenarioChange,
  dpsList,
  selectedDpsId,
  onDpsChange,
  dpsSlotCount,
  dpsScheduleStatus = 'idle',
  snapshotStatusItems,
  snapshotOverallStatus,
  onDpsShowDetails,
  drsList,
  selectedDrsId,
  onDrsChange,
  differentialScenarioList,
  selectedDifferentialScenarioId,
  onDifferentialScenarioChange,
  taxScenarioList,
  selectedTaxScenarioId,
  onTaxScenarioChange,
  expenseScenarioList,
  selectedExpenseScenarioId,
  onExpenseScenarioChange,
  priceDecks,
  selectedPriceDeckId,
  onPriceDeckChange,
  onShowPriceDeckDetails,
  onExportAries,
  isExportAriesDisabled,
  isExportAriesLoading,
  exportAriesTooltip,
  onExportAriesMdb,
  onExportContent,
  isExportContentDisabled,
  isExportContentLoading,
  exportContentTooltip,
  dcaScenarios,
  selectedDcaScenarioId,
  onDcaScenarioChange,
  evalMode = 'legacy',
  onEvalModeChange,
  evaluationCaseList,
  selectedEvaluationCaseId,
  onEvaluationCaseChange,
  evaluationCaseDetail,
}: Props) {
  const isEffectiveDateValid = moment(effectiveDate).isValid();

  const usFormatEffectiveDate = moment(effectiveDate).format(US_DATE_FORMAT);

  const dateRangeFilterType = useAtomValue(
    workingDateRangeFilterTypeAtom(packageId)
  );
  const absoluteMinDate = useAtomValue(workingAbsoluteMinDateAtom(packageId));
  const absoluteMaxDate = useAtomValue(workingAbsoluteMaxDateAtom(packageId));

  const isEffectiveDateLineVisible =
    dateRangeFilterType === 'relative' ||
    !appliedEffectiveDate ||
    (parseMomentUtc(appliedEffectiveDate).isSameOrAfter(absoluteMinDate) &&
      parseMomentUtc(appliedEffectiveDate).isSameOrBefore(absoluteMaxDate));

  // INS-8827: Scenario picker — list + selection are owned by the parent
  // (eval-nav-layout) so the picked scenario_id can flow into the BE
  // requests (POST /evaluations/calculate, POST /…/schedule). The BE only
  // consumes a single scenario_id, so the picker is a single-select
  // ComboBox.
  const selectedScenarioItem =
    selectedScenarioId !== null
      ? ((scenarios ?? []).find((s) => s.id === selectedScenarioId) ?? null)
      : null;

  const scenarioItemToString = (item: ScenariosResponse | null) => {
    if (!item) return '';
    return `${item.name} - ${capitalizeFirstLetter(item.interestType || '')}`;
  };

  const selectedDpsItem = dpsList?.find((d) => d.id === selectedDpsId) ?? null;

  const dpsItemToString = (item: DpsItem | null) => {
    return item
      ? `${item.name} | ${item.paceModel} | ${moment(item.referenceDate).format(
          'M/D/YYYY'
        )}`
      : '';
  };

  const selectedPriceDeckItem = selectedPriceDeckId
    ? (priceDecks?.find((d) => d.id === selectedPriceDeckId) ?? null)
    : SCENARIO_PRICING_ITEM;

  // Order: selected deck first (if any), then Scenario Pricing, then the rest
  const priceDeckDropdownItems = [
    ...(selectedPriceDeckItem && selectedPriceDeckItem !== SCENARIO_PRICING_ITEM
      ? [selectedPriceDeckItem]
      : []),
    SCENARIO_PRICING_ITEM,
    ...(priceDecks ?? []).filter(
      (d) => !selectedPriceDeckId || d.id !== selectedPriceDeckId
    ),
  ];

  const selectedDcaScenarioItem = selectedDcaScenarioId
    ? (dcaScenarios?.find((s) => s.id === selectedDcaScenarioId) ??
      LEGACY_DCA_ITEM)
    : LEGACY_DCA_ITEM;

  // The DCA picker hides on a case run, by the rule Calculate uses (isCaseRun).
  const caseSelected = isCaseRun({
    evalMode,
    evaluationCaseId: selectedEvaluationCaseId,
  });
  const dcaScenarioDropdownItems: DcaScenarioDropdownItem[] = [
    LEGACY_DCA_ITEM,
    ...(dcaScenarios ?? []),
  ];

  return (
    <div className={style.container}>
      <header className={style.header}>
        <div className={style.titleRow}>
          <span className={style.title}>Eval #{evalId}</span>
        </div>
      </header>
      {!isEffectiveDateLineVisible ? (
        <InlineNotification
          className={style.effectiveDateNotification}
          kind="warning"
          title="Date Range selected does not include Effective Date"
        ></InlineNotification>
      ) : null}
      <div className={style.settingsContainer}>
        <div className={style.eval2DropdownContainer}>
          {scenarios === undefined ? (
            <SkeletonText width="100%" />
          ) : (
            <Layer>
              <ComboBox<ScenariosResponse>
                id="eval3-scenarios"
                items={scenarios}
                titleText="Select Scenario"
                placeholder="Type to search scenarios"
                selectedItem={selectedScenarioItem}
                itemToString={scenarioItemToString}
                shouldFilterItem={({
                  item,
                  inputValue,
                }: {
                  item: ScenariosResponse;
                  inputValue: string | null;
                }) => {
                  if (!inputValue) return true;
                  return scenarioItemToString(item)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase());
                }}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: ScenariosResponse | null | undefined;
                }) => {
                  if (onScenarioChange) {
                    onScenarioChange(selectedItem?.id ?? null);
                  }
                }}
              />
            </Layer>
          )}
        </div>
        <Tabs
          selectedIndex={evalMode === 'case' ? 0 : 1}
          onChange={({ selectedIndex }) => {
            if (onEvalModeChange) {
              onEvalModeChange(selectedIndex === 0 ? 'case' : 'legacy');
            }
          }}
        >
          <TabList contained>
            <Tab>Evaluation Case</Tab>
            <Tab>Eval 2.0 Base</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <div className={style.caseTabContent}>
                <ComboBox
                  id="evaluation-case-dropdown"
                  titleText="Evaluation Case"
                  placeholder="Select case..."
                  items={evaluationCaseList ?? []}
                  selectedItem={
                    evaluationCaseList?.find(
                      (c) => c.id === selectedEvaluationCaseId
                    ) ?? null
                  }
                  itemToString={(item: EvaluationCaseListItem | null) =>
                    item
                      ? `${item.name} (${capitalizeFirstLetter(
                          item.evaluationType
                        )})`
                      : ''
                  }
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem: EvaluationCaseListItem | null | undefined;
                  }) => {
                    // Guard: only update on explicit user selection.
                    // Carbon ComboBox fires onChange(null) during internal
                    // state reconciliation on re-renders — ignore those.
                    if (onEvaluationCaseChange && selectedItem) {
                      onEvaluationCaseChange(selectedItem.id);
                    }
                  }}
                />
                {evaluationCaseDetail && (
                  <EvaluationCaseDomainChips detail={evaluationCaseDetail} />
                )}
              </div>
            </TabPanel>
            <TabPanel>
              <div className={style.dpsDropdownContainer}>
                <div className={style.dpsDropdownRow}>
                  <div style={{ flex: 1 }}>
                    {dpsList ? (
                      dpsList.length > 0 ? (
                        <ComboBox<DpsItem>
                          id="dps-dropdown"
                          titleText={
                            <span className={style.eval2Label}>
                              Development Pace Scenario
                              <FloatingTooltip
                                placement="right"
                                className={style.eval2Tooltip}
                                content={
                                  <p>
                                    Select a Development Pace Scenario to use
                                    for scheduling slot timing and generating
                                    the slots blob.
                                  </p>
                                }
                              >
                                <Information className={style.eval2InfoIcon} />
                              </FloatingTooltip>
                            </span>
                          }
                          placeholder="Search scenarios..."
                          items={dpsList}
                          selectedItem={selectedDpsItem}
                          itemToString={dpsItemToString}
                          shouldFilterItem={({
                            item,
                            inputValue,
                          }: {
                            item: DpsItem;
                            inputValue: string | null;
                          }) => {
                            if (!inputValue) return true;
                            return dpsItemToString(item)
                              .toLowerCase()
                              .includes(inputValue.toLowerCase());
                          }}
                          onChange={({
                            selectedItem: item,
                          }: {
                            selectedItem: DpsItem | null | undefined;
                          }) => {
                            if (item && onDpsChange) {
                              onDpsChange(item.id);
                            }
                          }}
                        />
                      ) : (
                        <InlineNotification
                          kind="info"
                          title="No DPS available"
                          subtitle="There are no Development Pace Scenarios for this package."
                          lowContrast
                          hideCloseButton
                        />
                      )
                    ) : (
                      <SkeletonText width="100%" />
                    )}
                  </div>
                  {(dpsScheduleStatus === 'scheduling' ||
                    dpsScheduleStatus === 'caching') &&
                    snapshotStatusItems &&
                    snapshotStatusItems.length > 0 && (
                      <WorkflowStatusPopover
                        statusItems={snapshotStatusItems}
                        overallStatus={snapshotOverallStatus ?? 'processing'}
                        iconDescription="Snapshot build progress"
                        ariaLabel="Show snapshot build progress"
                        dataTestId="dps-snapshot-progress"
                      />
                    )}
                  {(dpsScheduleStatus === 'scheduling' ||
                    dpsScheduleStatus === 'caching') &&
                    (!snapshotStatusItems ||
                      snapshotStatusItems.length === 0) && (
                      <InlineLoading
                        description="Scheduling slots..."
                        className={style.dpsSchedulingIndicator}
                      />
                    )}
                  {dpsScheduleStatus === 'ready' && (
                    <span className={style.dpsSlotCount}>
                      <Checkmark className={style.dpsReadyIcon} />
                      {dpsSlotCount && dpsSlotCount > 0
                        ? `${formatNumber({
                            value: dpsSlotCount,
                            minPrecision: 0,
                            includeCommas: true,
                          })} slot${dpsSlotCount !== 1 ? 's' : ''}`
                        : ''}
                    </span>
                  )}
                  {dpsScheduleStatus === 'error' && (
                    <span className={style.dpsScheduleError}>
                      Schedule failed
                    </span>
                  )}
                  {selectedDpsId && onDpsChange && (
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Renew}
                      disabled={
                        dpsScheduleStatus === 'scheduling' ||
                        dpsScheduleStatus === 'caching'
                      }
                      onClick={() => onDpsChange(selectedDpsId)}
                    >
                      Reschedule
                    </Button>
                  )}
                </div>
                {selectedDpsId && onDpsShowDetails && (
                  <Button
                    kind="ghost"
                    size="sm"
                    className={style.showDetailsLink}
                    onClick={onDpsShowDetails}
                  >
                    Show details
                  </Button>
                )}
              </div>
              <div className={style.dpsDropdownContainer}>
                {drsList ? (
                  drsList.length > 0 ? (
                    <ComboBox<DrsItem>
                      id="drs-dropdown"
                      titleText={
                        <span className={style.eval2Label}>
                          Discount Rate Scenario
                          <FloatingTooltip
                            placement="right"
                            className={style.eval2Tooltip}
                            content={
                              <p>
                                Select a Discount Rate Scenario to override
                                basin-tier discount rates per slot.
                              </p>
                            }
                          >
                            <Information className={style.eval2InfoIcon} />
                          </FloatingTooltip>
                        </span>
                      }
                      placeholder="Search scenarios..."
                      items={drsList}
                      selectedItem={
                        drsList.find((d) => d.id === selectedDrsId) ??
                        (selectedDrsId
                          ? {
                              id: selectedDrsId,
                              name: `Scenario #${selectedDrsId} (unavailable)`,
                              effectiveDate: null,
                            }
                          : null)
                      }
                      itemToString={(item: DrsItem | null) => {
                        if (!item) return '';
                        return `${item.name}${
                          item.effectiveDate ? ` · ${item.effectiveDate}` : ''
                        }`;
                      }}
                      shouldFilterItem={({
                        item,
                        inputValue,
                      }: {
                        item: DrsItem;
                        inputValue: string | null;
                      }) => {
                        if (!inputValue) return true;
                        return item.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase());
                      }}
                      onChange={({
                        selectedItem: item,
                      }: {
                        selectedItem: DrsItem | null | undefined;
                      }) => {
                        if (onDrsChange) {
                          onDrsChange(item?.id ?? null);
                        }
                      }}
                    />
                  ) : (
                    <InlineNotification
                      kind="info"
                      title="No DRS available"
                      subtitle="No Discount Rate Scenarios exist yet."
                      lowContrast
                      hideCloseButton
                    />
                  )
                ) : (
                  <SkeletonText width="100%" />
                )}
              </div>
              <div className={style.dpsDropdownContainer}>
                {differentialScenarioList ? (
                  differentialScenarioList.length > 0 ? (
                    <ComboBox<DifferentialScenarioItem>
                      id="differential-scenario-dropdown"
                      titleText={
                        <span className={style.eval2Label}>
                          Differential Scenario
                          <FloatingTooltip
                            placement="right"
                            className={style.eval2Tooltip}
                            content={
                              <p>
                                Select a Differential Scenario to apply
                                differential pricing overrides per slot.
                              </p>
                            }
                          >
                            <Information className={style.eval2InfoIcon} />
                          </FloatingTooltip>
                        </span>
                      }
                      placeholder="Search scenarios..."
                      items={differentialScenarioList}
                      selectedItem={
                        differentialScenarioList.find(
                          (d) => d.id === selectedDifferentialScenarioId
                        ) ??
                        (selectedDifferentialScenarioId
                          ? {
                              id: selectedDifferentialScenarioId,
                              name: `Scenario #${selectedDifferentialScenarioId} (unavailable)`,
                              effectiveDate: null,
                            }
                          : null)
                      }
                      itemToString={(item: DifferentialScenarioItem | null) => {
                        if (!item) return '';
                        return `${item.name}${
                          item.effectiveDate ? ` · ${item.effectiveDate}` : ''
                        }`;
                      }}
                      shouldFilterItem={({
                        item,
                        inputValue,
                      }: {
                        item: DifferentialScenarioItem;
                        inputValue: string | null;
                      }) => {
                        if (!inputValue) return true;
                        return item.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase());
                      }}
                      onChange={({
                        selectedItem: item,
                      }: {
                        selectedItem:
                          DifferentialScenarioItem | null | undefined;
                      }) => {
                        if (onDifferentialScenarioChange) {
                          onDifferentialScenarioChange(item?.id ?? null);
                        }
                      }}
                    />
                  ) : (
                    <InlineNotification
                      kind="info"
                      title="No Differential Scenarios available"
                      subtitle="No Differential Scenarios exist yet."
                      lowContrast
                      hideCloseButton
                    />
                  )
                ) : (
                  <SkeletonText width="100%" />
                )}
              </div>
              <div className={style.dpsDropdownContainer}>
                {taxScenarioList ? (
                  taxScenarioList.length > 0 ? (
                    <ComboBox<TaxScenarioItem>
                      id="tax-scenario-dropdown"
                      titleText={
                        <span className={style.eval2Label}>
                          Tax Scenario
                          <FloatingTooltip
                            placement="right"
                            className={style.eval2Tooltip}
                            content={
                              <p>
                                Select a Tax Scenario to override state tax
                                defaults per slot.
                              </p>
                            }
                          >
                            <Information className={style.eval2InfoIcon} />
                          </FloatingTooltip>
                        </span>
                      }
                      placeholder="Search scenarios..."
                      items={taxScenarioList}
                      selectedItem={
                        taxScenarioList.find(
                          (t) => t.id === selectedTaxScenarioId
                        ) ?? null
                      }
                      itemToString={(item: TaxScenarioItem | null) => {
                        if (!item) return '';
                        return `${item.name}${
                          item.effectiveDate ? ` · ${item.effectiveDate}` : ''
                        }`;
                      }}
                      shouldFilterItem={({
                        item,
                        inputValue,
                      }: {
                        item: TaxScenarioItem;
                        inputValue: string | null;
                      }) => {
                        if (!inputValue) return true;
                        return item.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase());
                      }}
                      onChange={({
                        selectedItem: item,
                      }: {
                        selectedItem: TaxScenarioItem | null | undefined;
                      }) => {
                        if (onTaxScenarioChange) {
                          onTaxScenarioChange(item?.id ?? null);
                        }
                      }}
                    />
                  ) : (
                    <InlineNotification
                      kind="info"
                      title="No Tax Scenarios available"
                      subtitle="No Tax Scenarios exist yet."
                      lowContrast
                      hideCloseButton
                    />
                  )
                ) : (
                  <SkeletonText width="100%" />
                )}
              </div>
              <div className={style.dpsDropdownContainer}>
                {expenseScenarioList ? (
                  expenseScenarioList.length > 0 ? (
                    <ComboBox<ExpenseScenarioItem>
                      id="expense-scenario-dropdown"
                      titleText={
                        <span className={style.eval2Label}>
                          Expense Scenario
                          <FloatingTooltip
                            placement="right"
                            className={style.eval2Tooltip}
                            content={
                              <p>
                                Select an Expense Scenario to override
                                opex/capex expense defaults per slot.
                              </p>
                            }
                          >
                            <Information className={style.eval2InfoIcon} />
                          </FloatingTooltip>
                        </span>
                      }
                      placeholder="Search scenarios..."
                      items={expenseScenarioList}
                      selectedItem={
                        expenseScenarioList.find(
                          (e) => e.id === selectedExpenseScenarioId
                        ) ??
                        (selectedExpenseScenarioId
                          ? {
                              id: selectedExpenseScenarioId,
                              name: `Scenario #${selectedExpenseScenarioId} (unavailable)`,
                              effectiveDate: null,
                            }
                          : null)
                      }
                      itemToString={(item: ExpenseScenarioItem | null) => {
                        if (!item) return '';
                        return `${item.name}${
                          item.effectiveDate ? ` · ${item.effectiveDate}` : ''
                        }`;
                      }}
                      shouldFilterItem={({
                        item,
                        inputValue,
                      }: {
                        item: ExpenseScenarioItem;
                        inputValue: string | null;
                      }) => {
                        if (!inputValue) return true;
                        return item.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase());
                      }}
                      onChange={({
                        selectedItem: item,
                      }: {
                        selectedItem: ExpenseScenarioItem | null | undefined;
                      }) => {
                        if (onExpenseScenarioChange) {
                          onExpenseScenarioChange(item?.id ?? null);
                        }
                      }}
                    />
                  ) : (
                    <InlineNotification
                      kind="info"
                      title="No Expense Scenarios available"
                      subtitle="No Expense Scenarios exist yet."
                      lowContrast
                      hideCloseButton
                    />
                  )
                ) : (
                  <SkeletonText width="100%" />
                )}
              </div>
              <div className={style.priceDeckDropdownContainer}>
                {priceDecks && (
                  <>
                    <Dropdown<PriceDeckItem>
                      id="price-deck-dropdown"
                      titleText="Price Deck"
                      label="Select Price Deck"
                      items={priceDeckDropdownItems}
                      selectedItem={selectedPriceDeckItem ?? undefined}
                      itemToString={(item: PriceDeckItem | null) =>
                        item?.name ?? ''
                      }
                      onChange={({
                        selectedItem: item,
                      }: {
                        selectedItem: PriceDeckItem | null;
                      }) => {
                        if (onPriceDeckChange) {
                          onPriceDeckChange(
                            item && item.id !== SCENARIO_PRICING_ITEM.id
                              ? item.id
                              : null
                          );
                        }
                      }}
                    />
                    {selectedPriceDeckId && onShowPriceDeckDetails && (
                      <Button
                        kind="ghost"
                        size="sm"
                        className={style.showDetailsLink}
                        onClick={onShowPriceDeckDetails}
                      >
                        Show details
                      </Button>
                    )}
                  </>
                )}
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
        {/* A case's run takes its DCA from the case (the server stamps it), so the pick is
            offered only for a run with no case, as on the eval page (eval-settings-form). */}
        {!caseSelected && (
          <div className={style.dcaScenarioDropdownContainer}>
            <Dropdown<DcaScenarioDropdownItem>
              id="dca-scenario-dropdown"
              titleText="DCA Scenario"
              label="Select DCA Scenario"
              items={dcaScenarioDropdownItems}
              selectedItem={selectedDcaScenarioItem}
              itemToString={(item: DcaScenarioDropdownItem | null) =>
                item?.name ?? ''
              }
              onChange={({
                selectedItem: item,
              }: {
                selectedItem: DcaScenarioDropdownItem | null;
              }) => {
                if (onDcaScenarioChange) {
                  onDcaScenarioChange(item?.id ?? null);
                }
              }}
            />
            {selectedDcaScenarioId && selectedDcaScenarioItem?.href && (
              <LaunchLink
                to={selectedDcaScenarioItem.href}
                className={style.showDetailsLink}
                label={`View scenario ${selectedDcaScenarioItem.name} in a new tab`}
              >
                View scenario
              </LaunchLink>
            )}
          </div>
        )}
        <div className={style.controlsRow}>
          <RadioButtonGroup
            legendText="Measurement Basis"
            name="measurement-basis"
            valueSelected={measurementType}
            onChange={(value) => {
              unstable_batchedUpdates(() => {
                setMeasurementType(value as MeasurementType);
              });
            }}
          >
            <RadioButton labelText="Net" value="net" id="net-radio" />
            <RadioButton labelText="Gross" value="gross" id="gross-radio" />
          </RadioButtonGroup>

          <RadioButtonGroup
            legendText="AFE Source"
            name="afe-source"
            valueSelected={slotAfeSource}
            onChange={(value) => {
              unstable_batchedUpdates(() => {
                setSlotAfeSource(value as SlotAfeSource);
              });
            }}
          >
            <RadioButton
              labelText={
                <>
                  AFE
                  <DefinitionTooltip
                    definition="Uses AFE smart defaults with per-slot cost and date overrides."
                    align="bottom"
                    className={style.infoTooltip}
                  >
                    <Information />
                  </DefinitionTooltip>
                </>
              }
              value="afe"
              id="afe-source-radio"
            />
            <RadioButton
              labelText={
                <>
                  Scenario
                  <DefinitionTooltip
                    definition="Uses scenario-based costs. Per-slot AFE and cost date overrides are disabled."
                    align="bottom"
                    className={style.infoTooltip}
                  >
                    <Information />
                  </DefinitionTooltip>
                </>
              }
              value="scenario"
              id="scenario-source-radio"
            />
          </RadioButtonGroup>

          <div className={style.datePickerContainer}>
            <DatePicker
              light
              datePickerType="single"
              value={isEffectiveDateValid ? usFormatEffectiveDate : undefined}
              onChange={([updatedDate]) => {
                unstable_batchedUpdates(() => {
                  const serverUpdatedDate =
                    moment(updatedDate).format(SERVER_DATE_FORMAT);
                  if (serverUpdatedDate !== effectiveDate) {
                    setEffectiveDate(serverUpdatedDate);
                  }
                });
              }}
            >
              <DatePickerInput
                size="sm"
                disabled={isLoading}
                placeholder={US_DATE_FORMAT}
                labelText="Effective Date"
                id="date-picker-single"
              />
            </DatePicker>
          </div>

          <Dropdown<Capitalize<`${FreeCashFlowAggregationPeriod}ly`>>
            light
            size="sm"
            className={style.dropdown}
            disabled={isLoading}
            id="interval"
            label=""
            items={['Monthly', 'Quarterly', 'Yearly']}
            titleText="Time Interval"
            selectedItem={capitalize(`${aggregation}ly`)}
            onChange={({ selectedItem }) => {
              unstable_batchedUpdates(() => {
                if (selectedItem) {
                  setAggregation(
                    selectedItem
                      .toLowerCase()
                      .slice(
                        0,
                        selectedItem.length - 2
                      ) as FreeCashFlowAggregationPeriod
                  );
                }
              });
            }}
          ></Dropdown>
        </div>

        <DateRangeFilter
          packageId={packageId}
          aggregation={aggregation}
          effectiveDate={
            appliedEffectiveDate ||
            parseMomentUtc(effectiveDate)
              .startOf('month')
              .format(SERVER_DATE_FORMAT)
          }
        />

        {(onExportContent || onExportAries || onExportAriesMdb) && (
          <div className={style.exportAriesRow}>
            {(isExportAriesLoading || isExportContentLoading) && (
              <InlineLoading description="Exporting..." />
            )}
            {onExportContent &&
              (exportContentTooltip ? (
                <FloatingTooltip
                  placement="top"
                  content={<p>{exportContentTooltip}</p>}
                >
                  <Button
                    kind="tertiary"
                    size="md"
                    renderIcon={Download}
                    onClick={onExportContent}
                    disabled={isExportContentDisabled || isExportContentLoading}
                  >
                    Export Content
                  </Button>
                </FloatingTooltip>
              ) : (
                <Button
                  kind="tertiary"
                  size="md"
                  renderIcon={Download}
                  onClick={onExportContent}
                  disabled={isExportContentDisabled || isExportContentLoading}
                >
                  Export Content
                </Button>
              ))}
            {onExportAries &&
              (exportAriesTooltip ? (
                <FloatingTooltip
                  placement="top"
                  content={<p>{exportAriesTooltip}</p>}
                >
                  <Button
                    kind="tertiary"
                    size="md"
                    renderIcon={Download}
                    onClick={onExportAries}
                    disabled={isExportAriesDisabled || isExportAriesLoading}
                  >
                    Export Aries Queries
                  </Button>
                </FloatingTooltip>
              ) : (
                <Button
                  kind="tertiary"
                  size="md"
                  renderIcon={Download}
                  onClick={onExportAries}
                  disabled={isExportAriesDisabled || isExportAriesLoading}
                >
                  Export Aries Queries
                </Button>
              ))}
            {onExportAriesMdb && (
              <Button
                kind="tertiary"
                size="md"
                renderIcon={Download}
                onClick={onExportAriesMdb}
              >
                Export Aries MDB
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
